/*
 * Prev/next arrows for the product media gallery.
 *
 * On desktop the thumbnail layouts hide every media item except `.is-active`
 * (see section-main-product.css), so the built-in slider arrows — which work by
 * scrolling the track — have nothing to scroll. Instead of duplicating
 * MediaGallery.setActiveMedia, these arrows click the adjacent thumbnail button.
 * That reuses the existing listener wired up in media-gallery.js and keeps
 * active state, aria-current, live-region announcements and video pausing
 * all consistent with a normal thumbnail click.
 */
if (!customElements.get('gallery-nav-arrows')) {
  customElements.define(
    'gallery-nav-arrows',
    class GalleryNavArrows extends HTMLElement {
      connectedCallback() {
        this.gallery = this.closest('media-gallery');
        if (!this.gallery) return;

        this.prevButton = this.querySelector('[data-gallery-direction="previous"]');
        this.nextButton = this.querySelector('[data-gallery-direction="next"]');

        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-gallery-direction]');
          if (!button) return;
          this.step(button.dataset.galleryDirection === 'next' ? 1 : -1);
        });

        // Keep the arrows hidden unless there is more than one thumbnail to move between.
        if (this.thumbnails.length < 2) {
          this.hidden = true;
          return;
        }

        // aria-current moves whenever the active media changes — by arrow, by
        // thumbnail click, by swiping the mobile slider, or by a variant change.
        // Watching it covers every route without hooking each one separately.
        const thumbnails = this.gallery.querySelector('[id^="GalleryThumbnails"]');
        if (thumbnails) {
          this.observer = new MutationObserver(() => this.syncDisabledState());
          this.observer.observe(thumbnails, {
            attributes: true,
            attributeFilter: ['aria-current'],
            subtree: true,
          });
        }

        this.syncDisabledState();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      get thumbnails() {
        if (!this.gallery) return [];
        return Array.from(this.gallery.querySelectorAll('[id^="GalleryThumbnails"] [data-target]'));
      }

      get currentIndex() {
        return this.thumbnails.findIndex(
          (thumbnail) => thumbnail.querySelector('button')?.getAttribute('aria-current') === 'true'
        );
      }

      syncDisabledState() {
        const total = this.thumbnails.length;
        const index = this.currentIndex < 0 ? 0 : this.currentIndex;

        if (this.prevButton) this.prevButton.disabled = index <= 0;
        if (this.nextButton) this.nextButton.disabled = index >= total - 1;
      }

      step(delta) {
        const thumbnails = this.thumbnails;
        if (thumbnails.length < 2) return;

        const index = (this.currentIndex < 0 ? 0 : this.currentIndex) + delta;
        // Stop at the ends rather than wrapping, so the disabled states are honest.
        if (index < 0 || index > thumbnails.length - 1) return;

        thumbnails[index].querySelector('button')?.click();
        this.syncDisabledState();
      }
    }
  );
}
